import * as React from "react";
import { JobList } from "./JobList";
import { JobFilters } from "./JobFilters";
import { Pagination } from "../../../shared/components/navigation";
import moment from "moment";
import type { SelectOption } from '@/shared/components/forms';

let isMobile = false;
if (typeof window !== 'undefined') {
  const { isMobile: _isMobile } = require('react-device-detect');
  isMobile = _isMobile;
}

if (typeof window !== 'undefined') {
  const { dotStream } = require('ldrs');
  dotStream.register();
}

// Design tokens - consistent with other job components
const tokens = {
  colors: {
    primary: '#3498db',
    primaryDark: '#2980b9',
    text: '#1a365d',
    textMuted: '#64748b',
    border: '#e2e8f0',
    bg: '#ffffff',
    bgSubtle: '#f8fafc',
    bgPage: '#f1f5f9',
  },
  radius: {
    md: '10px',
    lg: '14px',
  },
  shadow: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.06)',
    md: '0 4px 12px rgba(0, 0, 0, 0.08)',
  },
};

// TypeScript declarations for global utilities
declare global {
  interface Window {
    DDSJobs?: {
      isBot: () => boolean;
    };
    Sentry?: {
      captureException: (error: Error, context?: any) => void;
    };
  }
}

// Make moment available globally for legacy components
declare global {
  interface Window {
    moment: typeof moment;
  }
}

if (typeof window !== 'undefined') {
  window.moment = moment;
}

const URI = "/jobsearch.json";

interface JobType {
  id: number;
  group: string;
  title: string;
}

interface FormElements {
  position_type_eq_any: string[];
  job_type_ids?: string[];  // Store job type IDs in state
  temp_date_eq?: string;
  [key: string]: any;
}

interface JobsProps {
  form_elements?: FormElements;
  location?: string;
  authenticity_token: string;
  job_types: JobType[];
}

interface DateChangeProps {
  date: moment.Moment;
  fieldName?: string;
}

function changeDate({ date, fieldName = "date" }: DateChangeProps) {
  const m = moment.isMoment(date) ? date : moment(date);
  if (!m.isValid()) return { position_type_eq_any: ["temp"] };
  const dateObject = {
    [fieldName]: m.format("YYYY-MM-DD"),
    position_type_eq_any: ["temp"],
  };
  return dateObject;
}

const Jobs: React.FC<JobsProps> = (props) => {
  const { job_types, authenticity_token } = props;

  // Create lookup map: job type ID -> group ID
  const jobTypeIdToGroupMap = React.useMemo(() => {
    return new Map(job_types.map(jt => [jt.id.toString(), jt.group]));
  }, [job_types]);

  const [jobs, setJobs] = React.useState<any[]>([]);
  const [formElements, setFormElements] = React.useState<FormElements>({
    ...props.form_elements,
    position_type_eq_any: ["fulltime", "parttime", "longtermtemp", "temp"],
  });
  const [sort, setSort] = React.useState("newest");
  const [location, setLocation] = React.useState(props.location || "");
  const [page, setPage] = React.useState(1);
  const [miles, setMiles] = React.useState(100);
  const [loaded, setLoaded] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState<number | undefined>(undefined);
  const [totalPages, setTotalPages] = React.useState<number | undefined>(undefined);
  const [records, setRecords] = React.useState<number | undefined>(undefined);
  const [prevPage, setPrevPage] = React.useState<number | undefined>(undefined);
  const [nextPage, setNextPage] = React.useState<number | undefined>(undefined);

  const sendQuery = React.useCallback(() => {
    // Check if this is a bot to prevent unnecessary API calls
    if (window.DDSJobs && window.DDSJobs.isBot()) {
      console.warn('Bot detected, skipping job search API call');
      setLoaded(true);
      return;
    }

    // Convert job type IDs to group IDs for API
    const searchParams = { ...formElements };
    if (formElements.job_type_ids && formElements.job_type_ids.length > 0) {
      const groupIds = formElements.job_type_ids
        .map(id => jobTypeIdToGroupMap.get(id))
        .filter(Boolean) as string[];
      const uniqueGroupIds = [...new Set(groupIds)];
      searchParams.job_type_group_eq_any = uniqueGroupIds;
      delete searchParams.job_type_ids;  // Remove job_type_ids, send only group IDs
    }

    const body = JSON.stringify({
      page,
      sort,
      location,
      miles,
      search: searchParams,
    });

    // Get CSRF token from meta tag
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

    const headers: HeadersInit = {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
    };

    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }

    fetch(URI, { method: "POST", headers, body })
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then(res => {
        // Validate response format
        if (!res || typeof res !== 'object') {
          throw new Error('Invalid response format from job search API');
        }

        const { jobs, total_pages, records } = res;
        const current_page = res["current_page="];

        // Validate critical fields
        if (!Array.isArray(jobs)) {
          console.warn('Jobs is not an array, using empty array:', jobs);
        }

        const prev_page = current_page === 1 ? current_page : current_page - 1;
        const next_page =
          current_page === total_pages ? current_page : current_page + 1;

        setJobs(Array.isArray(jobs) ? jobs : []);
        setCurrentPage(typeof current_page === 'number' ? current_page : 1);
        setTotalPages(typeof total_pages === 'number' ? total_pages : 1);
        setRecords(typeof records === 'number' ? records : 0);
        setPrevPage(prev_page);
        setNextPage(next_page);
        setLoaded(true);
      })
      .catch(err => {
        console.error('Error fetching jobs:', err);

        // Report to Sentry if available
        if (typeof window.Sentry !== 'undefined') {
          window.Sentry.captureException(err, {
            tags: {
              component: 'job_search',
              page: page,
              location: location
            },
            extra: {
              formElements,
              sort,
              miles,
              request_body: body
            }
          });
        }

        // Reset to safe state
        setJobs([]);
        setLoaded(true);
        setCurrentPage(1);
        setTotalPages(1);
        setRecords(0);
        setPrevPage(1);
        setNextPage(1);
      });
  }, [page, sort, location, miles, formElements, jobTypeIdToGroupMap]);

  React.useEffect(() => {
    sendQuery();
  }, [sendQuery]);

  const onChangeCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = event.target;
    let new_position_type_eq_any = [...formElements.position_type_eq_any];

    if (checked) {
      if (!new_position_type_eq_any.includes(value)) {
        new_position_type_eq_any.push(value);
      }
    } else {
      new_position_type_eq_any = new_position_type_eq_any.filter(v => v !== value);
    }

    // Resets Temp Date Filter
    const not_temp = ["fulltime", "parttime", "longtermtemp"].some(r =>
      new_position_type_eq_any.includes(r)
    );

    const reset_temp_date = not_temp ? { temp_date_eq: "" } : {};

    setFormElements({
      ...formElements,
      ...reset_temp_date,
      position_type_eq_any: new_position_type_eq_any,
    });
  };

  const onChangeLocation = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;

    if (name === "miles") {
      setMiles(parseInt(value));
      return;
    }

    if (name === "location") {
      const re = /^[0-9\b]+$/;
      const isZipCode = value.length <= 5 && (value === "" || re.test(value));
      if (isZipCode) {
        setLocation(value);
      }
    }
  };

  const onChangeDate = (props: DateChangeProps) => {
    setFormElements({
      ...formElements,
      ...changeDate(props),
    });
  };

  const onChangeSort = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const sortValue = event.target.value;
    const stateObject = sortValue.includes("temp")
      ? {
          sort: sortValue,
          form_elements: {
            ...formElements,
            position_type_eq_any: ["temp"],
          },
        }
      : { sort: sortValue };

    setSort(stateObject.sort);
    if (stateObject.form_elements) {
      setFormElements(stateObject.form_elements);
    }
  };

  const setForm = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = event.target;
    setFormElements({ ...formElements, [name]: value });
    setPage(1);
  };

  const onChangeJobTypes = (selectedIds: string[]) => {
    // Store job type IDs directly in state
    setFormElements({ ...formElements, job_type_ids: selectedIds });
    setPage(1);
  };

  const navigateTo = (newPage: number) => {
    setPage(newPage);
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 100);
    }
  };

  return (
    <div className="container browsing-font px-3" style={{
      paddingTop: '2rem',
      paddingBottom: '2rem',
    }}>
      <div className="row">
        {isMobile && (
          <div className="col-12 mb-4 text-center">
            <button
              type="button"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.625rem',
                padding: '0.875rem 1.75rem',
                borderRadius: tokens.radius.md,
                fontWeight: 600,
                fontSize: '0.9375rem',
                background: `linear-gradient(135deg, ${tokens.colors.primary}, ${tokens.colors.primaryDark})`,
                color: 'white',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 12px rgba(52, 152, 219, 0.3)',
              }}
              data-bs-toggle="modal"
              data-bs-target="#filtersModal"
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(52, 152, 219, 0.4)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(52, 152, 219, 0.3)';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
              </svg>
              Search Filters
            </button>

            <div
              className="modal fade"
              id="filtersModal"
              tabIndex={-1}
              aria-labelledby="filtersModalLabel"
              aria-hidden="true"
            >
              <div className="modal-dialog modal-dialog-scrollable">
                <div className="modal-content" style={{ borderRadius: tokens.radius.lg, overflow: 'hidden' }}>
                  <div className="modal-header" style={{
                    background: `linear-gradient(135deg, ${tokens.colors.primary}, ${tokens.colors.primaryDark})`,
                    color: 'white',
                    border: 'none',
                  }}>
                    <h5 className="modal-title" id="filtersModalLabel" style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.625rem',
                      fontWeight: 600,
                    }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                      </svg>
                      Search Filters
                    </h5>
                    <button
                      type="button"
                      className="btn-close btn-close-white"
                      data-bs-dismiss="modal"
                      aria-label="Close"
                    ></button>
                  </div>
                  <div className="modal-body" style={{ padding: 0 }}>
                    <JobFilters
                      form_elements={formElements}
                      job_types={job_types}
                      location={location}
                      miles={miles}
                      onChangeSort={onChangeSort}
                      onChangeLocation={onChangeLocation}
                      onChangeCheckbox={onChangeCheckbox}
                      onChangeDate={onChangeDate}
                      onChange={setForm}
                      onChangeJobTypes={onChangeJobTypes}
                    />
                  </div>
                  <div className="modal-footer" style={{
                    borderTop: `1px solid ${tokens.colors.border}`,
                    background: tokens.colors.bgSubtle,
                  }}>
                    <button
                      type="button"
                      style={{
                        padding: '0.625rem 1.5rem',
                        borderRadius: tokens.radius.md,
                        fontWeight: 600,
                        fontSize: '0.9375rem',
                        background: `linear-gradient(135deg, ${tokens.colors.primary}, ${tokens.colors.primaryDark})`,
                        color: 'white',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                      data-bs-dismiss="modal"
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div
          className="col-12 col-lg-8 mb-4"
          id="job-list"
        >
          <div style={{
            background: tokens.colors.bg,
            border: `1px solid ${tokens.colors.border}`,
            borderRadius: tokens.radius.lg,
            boxShadow: tokens.shadow.md,
            overflow: 'hidden',
          }}>
            {jobs.length > 0 && <JobList jobs={jobs} zipjobs={[]} sort={sort} records={records} />}
            {jobs.length < 1 && !loaded && <LoadingResults />}
            {jobs.length < 1 && loaded && <NoResults />}
          </div>
        </div>

        <div className="col-12 col-lg-4" id="job-filters">
          {!isMobile && (
            <JobFilters
              form_elements={formElements}
              job_types={job_types}
              location={location}
              miles={miles}
              onChangeSort={onChangeSort}
              onChangeLocation={onChangeLocation}
              onChangeCheckbox={onChangeCheckbox}
              onChangeDate={onChangeDate}
              onChange={setForm}
              onChangeJobTypes={onChangeJobTypes}
            />
          )}
        </div>

        {jobs.length > 0 && totalPages && currentPage && prevPage && nextPage && (
          <div className="col-12 mt-3">
            <Pagination
              current_page={currentPage}
              total_pages={totalPages}
              records={records || 0}
              prev_page={prevPage}
              next_page={nextPage}
              navigateTo={navigateTo}
            />
          </div>
        )}
      </div>
    </div>
  );
};

function LoadingResults() {
  return (
    <div style={{
      padding: '5rem 2rem',
      textAlign: 'center',
      background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
    }}>
      <div style={{
        width: '64px',
        height: '64px',
        margin: '0 auto 1.5rem',
        borderRadius: '16px',
        background: 'linear-gradient(135deg, #3498db, #2980b9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 4px 16px rgba(52, 152, 219, 0.25)',
      }}>
        <l-dot-stream
          size="32"
          speed="2.5"
          color="white"
        ></l-dot-stream>
      </div>
      <h4 style={{
        color: '#1a365d',
        fontWeight: 700,
        marginBottom: '0.5rem',
        fontSize: '1.25rem',
        letterSpacing: '-0.01em',
      }}>
        Finding Opportunities...
      </h4>
      <p style={{
        color: '#64748b',
        margin: 0,
        fontSize: '0.9375rem',
      }}>
        Searching for the perfect positions
      </p>
    </div>
  );
}

function NoResults() {
  return (
    <div style={{
      padding: '5rem 2rem',
      textAlign: 'center',
      background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
    }}>
      <div style={{
        width: '80px',
        height: '80px',
        margin: '0 auto 1.5rem',
        borderRadius: '20px',
        background: '#f1f5f9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"/>
          <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          <line x1="8" y1="11" x2="14" y2="11"/>
        </svg>
      </div>
      <h4 style={{
        color: '#1a365d',
        fontWeight: 700,
        marginBottom: '0.75rem',
        fontSize: '1.25rem',
        letterSpacing: '-0.01em',
      }}>
        No Jobs Found
      </h4>
      <p style={{
        color: '#64748b',
        maxWidth: '360px',
        margin: '0 auto 1.75rem',
        lineHeight: 1.6,
        fontSize: '0.9375rem',
      }}>
        We couldn't find any jobs matching your criteria. Try adjusting your filters or location.
      </p>
      <a
        href="/jobs"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.75rem 1.5rem',
          borderRadius: '10px',
          fontWeight: 600,
          fontSize: '0.9375rem',
          textDecoration: 'none',
          background: 'linear-gradient(135deg, #3498db, #2980b9)',
          color: 'white',
          border: 'none',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          boxShadow: '0 4px 12px rgba(52, 152, 219, 0.3)',
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(52, 152, 219, 0.4)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(52, 152, 219, 0.3)';
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="1 4 1 10 7 10"/>
          <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
        </svg>
        Reset Filters
      </a>
    </div>
  );
}

export default Jobs;
